<script setup lang="ts">
import { Loader2 } from 'lucide-vue-next'

import axios from 'axios'

import TabletSettings from './components/TabletSettings.vue'
import TabletsList from './components/TabletsList.vue'
import TabletTexts from './components/TabletTexts.vue'

import useTabletTabs from './composables/tabletPageTabs'

import LoyaltyTablet from '@/views/loyalty/components/LoyaltyTablet.vue'

import type { NotificationConfig } from '@/composables/merchantNotificationConfigs'
import type { LoyaltyMerchantFormConfigSection } from '@/types/global'
import type { MerchantColors, Tablet, TabletIdentifiers } from '@/types/types'

const { selectedStore } = useStore()
const notification = useNotification()
const { t } = useI18n()
const { getTablets, getTabletsOfStore, getTabletIdentifiers, regenerateTabletIdentifiers } = useTablet()
const { getMerchantColors, merchantColorsForm, updateMerchantColors } = useMerchantColors()
const { getMerchantMedias, merchantMediaForm, updateMerchantFormMedias } = useMerchantMedias()
const { fillWebappRegistrationTexts, getMerchantTexts, merchantTextsForm, patchMerchantTextsFromTabletForm } =
  useMerchantTexts()
const { getMerchantConfigs, merchantConfigsForm, updateMerchantConfigsForm, parseConfigFields } = useMerchantConfigs()

const { tabs, currentTab } = useTabletTabs()
const isLoaded = ref(false)
const isSaving = ref(false)
const tablets = ref(<Tablet[]>[])
const tabletIdentifiers = ref(<TabletIdentifiers>{})
const tabletConfigs = ref(<LoyaltyMerchantFormConfigSection[]>{})
const merchantColors = ref(<MerchantColors>{})
const notificationToUpdate = ref<NotificationConfig | null>(null)

const pageTitle = {
  title: 'pages.marketplace.tabletsPage.title'
}

const fetchTablets = async () => {
  if (selectedStore.value > 0) {
    tablets.value = await getTabletsOfStore(selectedStore.value)
  } else {
    tablets.value = await getTablets()
  }
  return tablets.value
}

const handleFetchTablets = async () => {
  tablets.value = await fetchTablets()
  isLoaded.value = true
}

const handleRegenerateTabletIdentifiers = async () => {
  tabletIdentifiers.value.code = await regenerateTabletIdentifiers()
}

const handleFilter = async () => {
  await handleFetchTablets()
}

const handleGetMerchantColors = async () => {
  merchantColors.value = await getMerchantColors()
  for (const [key] of Object.entries(merchantColorsForm.value)) {
    merchantColorsForm.value[key].value = merchantColors.value[key]
  }
}

const handleGetMerchantMedias = async () => {
  const merchantMedia = await getMerchantMedias()
  for (const [key] of Object.entries(merchantMediaForm.value)) {
    merchantMediaForm.value[key].pictureUrl = merchantMedia[key]
  }
}

const handleGetMerchantTexts = async () => {
  const merchantTexts = await getMerchantTexts()

  for (let key in merchantTextsForm.value) {
    if (Object.prototype.hasOwnProperty.call(merchantTextsForm.value, key)) {
      if (typeof merchantTextsForm.value[key] === 'object') {
        for (let subKey in merchantTextsForm.value[key]) {
          if (Object.prototype.hasOwnProperty.call(merchantTextsForm.value[key], subKey)) {
            merchantTextsForm.value[key][subKey].value = merchantTexts[subKey]
          }
        }
      } else {
        merchantTextsForm.value[key].value = merchantTexts[key]
      }
    }
  }

  // The generic copy above leaves null in these fields when the merchant has no custom text
  fillWebappRegistrationTexts(merchantTexts)
}

const handleGetMerchantConfigs = async () => {
  const merchantConfigs = await getMerchantConfigs()
  for (const [key, field] of Object.entries(merchantConfigsForm.value)) {
    if (Object.prototype.hasOwnProperty.call(merchantConfigs, key)) {
      field.value = merchantConfigs[key]
    }
  }
  tabletConfigs.value = [
    {
      title: t('pages.marketplace.tabletsPage.tabletConfig.title'),
      items: parseConfigFields(merchantConfigsForm.value).tablet
    }
  ]
}

const createNotificationConfig = async (notificationConfig: NotificationConfig) => {
  if (notificationConfig.is_active) {
    try {
      await axios.post('/notifications', notificationConfig)

      notification.create('success', {
        text: t('pages.loyaltyPage.notifications.created'),
        timeout: 3000
      })
    } catch (error) {
      if (error.response?.status === 422) {
        notification.create('error', {
          text: t('pages.loyaltyPage.notifications.create.error'),
          timeout: 3000
        })
      }
      throw error
    }
  }
}

const updateNotificationConfig = async (notificationConfig: NotificationConfig) => {
  try {
    await axios.put(`/notifications/${notificationConfig.id}`, notificationConfig)

    notification.create('success', {
      text: t('pages.loyaltyPage.notifications.updated')
    })
  } catch (error) {
    if (error.response?.status === 422) {
      notification.create('error', {
        text: t('pages.loyaltyPage.notifications.update.error')
      })
    }
    throw error
  }
}

const handleNotificationConfig = async () => {
  if (notificationToUpdate.value?.id) {
    await updateNotificationConfig(notificationToUpdate.value as NotificationConfig)
  } else {
    await createNotificationConfig(notificationToUpdate.value as NotificationConfig)
  }
}

const handleSaveNotificationConfig = async () => {
  isSaving.value = true

  try {
    await handleNotificationConfig()
  } catch {
    isSaving.value = false
    return
  }
}
const handleSave = async () => {
  await handleSaveNotificationConfig()

  await Promise.all([
    updateMerchantConfigsForm(),
    patchMerchantTextsFromTabletForm(),
    updateMerchantColors(),
    updateMerchantFormMedias()
  ])
    .then(() => {
      notification.create('success', {
        text: t('pages.marketplace.tabletsPage.configurationUpdateSuccess')
      })
    })
    .finally(() => {
      isSaving.value = false
    })
  return
}

onMounted(async () => {
  await Promise.all([
    handleGetMerchantColors(),
    handleGetMerchantMedias(),
    handleGetMerchantTexts(),
    handleGetMerchantConfigs(),
    handleFetchTablets(),
    (tabletIdentifiers.value = await getTabletIdentifiers())
  ])

  isLoaded.value = true
})
</script>

<template>
  <div v-if="isLoaded">
    <TheListHeader :title="t(pageTitle.title)">
      <template #actions>
        <Button v-if="currentTab !== 'tablets'" variant="default-primary" :disabled="isSaving" @click="handleSave">
          <Loader2 v-if="isSaving" class="animate-spin" />
          {{ t('pages.loyaltySupports.tablets.buttons.save') }}
        </Button>
      </template>
    </TheListHeader>
    <div class="mb-4 px-6 sm:px-12">
      <BaseTab v-model="currentTab" :tabs="tabs" />
    </div>
    <div class="divide-border px-6 pb-6 sm:px-12 sm:pb-12">
      <TabletsList
        v-if="currentTab === 'tablets'"
        :tablets="tablets"
        :tablet-identifiers="tabletIdentifiers"
        @on-fetch-data="handleFetchTablets()"
        @on-regenerate-code-request="handleRegenerateTabletIdentifiers"
      >
        <template #filters>
          <ThePageSettings display-store-selector @store-selected="handleFilter" />
        </template>
      </TabletsList>
      <LoyaltyTablet
        v-if="currentTab === 'appearance'"
        :merchant-medias="merchantMediaForm"
        :merchant-colors="merchantColorsForm"
        :merchant-texts="merchantTextsForm"
      />
      <TabletTexts v-if="currentTab === 'texts'" v-model:merchant-text="merchantTextsForm" />
      <TabletSettings
        v-if="currentTab === 'config'"
        :configs="tabletConfigs"
        @update:notification="notificationToUpdate = $event"
      />
    </div>
  </div>
  <TheLoadingPage v-else />
</template>
